import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  List,
  Typography,
  Space,
  Button,
  Grid,
  Tag,
  Dropdown,
  message,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  PhoneOutlined,
  MessageOutlined,
  TagOutlined,
} from '@ant-design/icons';
import type { AdministratorLead } from '../../types/administratorLeads';
import { DataTable, type AntDColumn } from '../common/DataTable';
import { formatDateLocale } from '../../utils/date';
import { useAuth } from '../../contexts/AuthContext';
import { generateWhatsAppMessage, createWhatsAppUrl } from '../../utils/whatsappMessage';
import { getFirstName, getFullName } from '../../utils/text';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface AdministratorLeadsTableProps {
  leads: AdministratorLead[];
  onEdit: (lead: AdministratorLead) => void;
  onDelete: (lead: AdministratorLead) => void;
  onView: (lead: AdministratorLead) => void;
  onAddOpportunity: (lead: AdministratorLead) => void;
  onUpdateStatus?: (leadId: string, status: string) => void;
  onToggleWhatsApp?: (leadId: string, unavailable: boolean) => void;
  onOpenDetailDrawer?: (lead: AdministratorLead) => void;
  loading?: boolean;
  pagination?: any;
  onTableChange?: (pagination: any) => void;
}

const AdministratorLeadsTable: React.FC<AdministratorLeadsTableProps> = ({
  leads,
  onEdit,
  onDelete,
  onView,
  onAddOpportunity,
  onUpdateStatus,
  onToggleWhatsApp,
  onOpenDetailDrawer,
  loading = false,
  pagination,
  onTableChange,
}) => {
  const { t, i18n } = useTranslation();
  const { firestoreUser } = useAuth();
  const screens = useBreakpoint();
  const lang = i18n.language || 'en-US';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
        return 'blue';
      case 'Contacted':
        return 'green';
      case 'Lost':
        return 'red';
      case 'Disqualified':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'MINVU Condominios':
        return 'purple';
      case 'Website':
        return 'blue';
      case 'Referral':
        return 'green';
      case 'Social Media':
        return 'cyan';
      case 'Google Places':
        return 'orange';
      default:
        return 'default';
    }
  };


  const handleEmailClick = (email: string, leadName?: string, leadId?: string) => {
    if (email) {
      const firstName = getFirstName(leadName || 'Administrador');
      const subject = `${firstName}, ¿eres administrador de condominios? Este correo es para ti.`;
      
      const body = `Hola ${firstName},

Encontré tu contacto a través del registro de administradores de condominios del Minvu y quería ponerme en contacto contigo.

Mi nombre es Dafne Palacios, y gestiono las relaciones con administradores de apartamentos para Chile en My Apartment App.

Ofrecemos un software de administración de condominios que ayuda a los administradores a calcular y cobrar gastos comunes, gestionar residentes, empleados y proveedores, y mantener el control de contabilidad y cumplimiento normativo, todo en una sola plataforma. Más información en https://myapartment.app/cl.html 

Actualmente estamos ejecutando un programa de referidos: si recomiendas My Apartment App para tu condominio o a otra comunidad cercana, Obtén hasta $100.000CLP en comisión por referencia.

Es nuestra forma de agradecerte por ayudarnos a crecer mientras apoyamos a administradores como tú con mejores herramientas.

Si te interesa, puedes agendar una llamada rápida de 15 minutos conmigo aquí: https://calendly.com/myapartment-support/15min

Saludos cordiales,

---
Si no deseas recibir más correos electrónicos, puedes darte de baja aquí: https://internal.myapartment.app/unsubscribe?token=${leadId || 'unknown'}&email=${encodeURIComponent(email)}
(Al darte de baja, tu registro será marcado como "Perdido" en nuestro sistema)`;


      // Detect mobile device and use appropriate Gmail URL
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      let gmailUrl;
      if (isMobile) {
        // Use Gmail app URL scheme for mobile devices
        gmailUrl = `googlegmail://co?to=${encodeURIComponent(email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      } else {
        // Use web Gmail for desktop
        gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }
      
      // Try to open Gmail app, fallback to web if it fails
      const gmailWindow = window.open(gmailUrl, '_blank');
      
      // If mobile and Gmail app didn't open, fallback to web Gmail
      if (isMobile && gmailWindow) {
        setTimeout(() => {
          if (gmailWindow.closed || gmailWindow.location.href === 'about:blank') {
            const webGmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            window.open(webGmailUrl, '_blank');
          }
        }, 1000);
      }
      
      // Update status to Contacted when email is opened
      if (onUpdateStatus && leadId) {
        onUpdateStatus(leadId, 'Contacted');
        message.success(t('sales:administratorLeads.messages.emailSent', 'Email opened'));
      }
    }
  };

  const handleCallClick = (phone: string, lead: AdministratorLead) => {
    if (!phone) return;

    // Open detail drawer with call action
    if (onOpenDetailDrawer) {
      onOpenDetailDrawer(lead);
    } else {
      // Fallback to direct call if no drawer handler
      let cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.length === 10) {
        cleanPhone = '1' + cleanPhone;
      } else if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
        // Already has US country code
      } else if (cleanPhone.length === 9 && cleanPhone.startsWith('9')) {
        cleanPhone = '56' + cleanPhone;
      } else if (cleanPhone.length === 8 && cleanPhone.startsWith('2')) {
        cleanPhone = '56' + cleanPhone;
      }
      const telUrl = `tel:+${cleanPhone}`;
      window.open(telUrl);
    }
  };

  const handleWhatsAppClick = (phone: string, lead: AdministratorLead) => {
    if (!phone) return;

    // Open detail drawer with WhatsApp action
    if (onOpenDetailDrawer) {
      onOpenDetailDrawer(lead);
    } else {
      // Fallback to direct WhatsApp if no drawer handler
      // Get the first name from the lead name and convert to proper case
      const firstName = getFirstName(lead.name || 'Administrator');
      
      // Get the user's name from auth context and convert to proper case
      const userName = getFirstName(firestoreUser?.displayName || 'Sales Manager');
      
      // Debug logging
      console.log('WhatsApp Debug (Table):', {
        originalLeadName: lead.name,
        convertedFirstName: firstName,
        originalUserDisplayName: firestoreUser?.displayName,
        convertedUserName: userName
      });
      
      // Generate the WhatsApp message using the utility function
      const whatsappMessage = generateWhatsAppMessage(t, firstName, userName);
      
      // Create WhatsApp URL with message
      const whatsappUrl = createWhatsAppUrl(phone, whatsappMessage);
      
      window.open(whatsappUrl, '_blank');
      
      if (onUpdateStatus) {
        onUpdateStatus(lead.id, 'Contacted');
        message.success(t('sales:administratorLeads.messages.whatsappSent', 'WhatsApp message opened'));
      }
    }
  };

  const handleToggleWhatsApp = (leadId: string, currentStatus: boolean) => {
    if (onToggleWhatsApp) {
      onToggleWhatsApp(leadId, !currentStatus);
      message.success(
        !currentStatus 
          ? t('sales:administratorLeads.messages.whatsappTagged', 'Marked as WhatsApp unavailable')
          : t('sales:administratorLeads.messages.whatsappAvailable', 'Marked as WhatsApp available')
      );
    }
  };

  const columns: AntDColumn<AdministratorLead>[] = [
    {
      id: 'name',
      label: t('sales:administratorLeads.table.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (_, record: AdministratorLead) => (
        <Button type="link" onClick={() => onView(record)} style={{ padding: 0, height: 'auto' }}>
          {record.name || 'No Name'}
        </Button>
      ),
    },
    {
      id: 'phone',
      label: t('sales:administratorLeads.table.phone', 'Phone'),
      key: 'phone',
      width: 200,
      render: (_, record: AdministratorLead) => {
        const primaryContact = record.contacts?.find(c => c.type === 'Primary');
        const phone = primaryContact?.phone;
        
        if (!phone) return '-';
        
        const isWhatsAppUnavailable = record.whatsapp_unavailable;
        
        const phoneActions = [
          {
            key: 'call',
            label: t('common:call', 'Call'),
            icon: <PhoneOutlined />,
            onClick: () => handleCallClick(phone, record),
          },
          {
            key: 'whatsapp',
            label: t('common:whatsapp', 'WhatsApp'),
            icon: <MessageOutlined />,
            disabled: isWhatsAppUnavailable,
            onClick: () => handleWhatsAppClick(phone, record),
          },
          {
            key: 'tag',
            label: isWhatsAppUnavailable 
              ? t('sales:administratorLeads.actions.markWhatsAppAvailable', 'Mark WhatsApp Available')
              : t('sales:administratorLeads.actions.markWhatsAppUnavailable', 'Mark WhatsApp Unavailable'),
            icon: <TagOutlined />,
            onClick: () => handleToggleWhatsApp(record.id, isWhatsAppUnavailable || false),
          },
        ];

        return (
          <Space onClick={(e) => e.stopPropagation()}>
            <Text style={{ color: isWhatsAppUnavailable ? '#999' : '#52c41a' }}>
              {phone}
            </Text>
            <Dropdown
              menu={{ items: phoneActions }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                type="text" 
                size="small" 
                icon={<MessageOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
            {isWhatsAppUnavailable && (
              <Tag color="red">
                {t('sales:administratorLeads.tags.noWhatsApp', 'No WhatsApp')}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      id: 'email',
      label: t('sales:administratorLeads.table.email', 'Email'),
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (_, record: AdministratorLead) => {
        const primaryContact = record.contacts?.find(c => c.type === 'Primary');
        const email = primaryContact?.email;
        return email ? (
          <Button 
            type="link" 
            onClick={(e) => {
              e.stopPropagation();
              handleEmailClick(email, record.name, record.id);
            }}
            style={{ padding: 0, height: 'auto', color: '#1890ff' }}
          >
            {email}
          </Button>
        ) : '-';
      },
    },
    {
      id: 'status',
      label: t('sales:administratorLeads.table.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {t(`sales:administratorLeads.status.${status.toLowerCase()}`, status)}
        </Tag>
      ),
    },
  ];

  // Mobile card view
  if (!screens.md) {
    return (
      <List
        dataSource={leads}
        loading={loading}
        pagination={pagination}
        renderItem={(lead: AdministratorLead) => {
          const primaryContact = lead.contacts?.find(c => c.type === 'Primary');
          return (
            <List.Item>
              <Card
                title={
                  <Button type="link" onClick={() => onView(lead)} style={{ padding: 0, height: 'auto' }}>
                    {getFullName(lead.name || 'No Name')}
                  </Button>
                }
                style={{ width: '100%' }}
                actions={[
                  <Button 
                    key="view" 
                    type="link" 
                    icon={<EyeOutlined />} 
                    onClick={() => onView(lead)}
                  >
                    {t('common:view')}
                  </Button>,
                  <Button 
                    key="edit" 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => onEdit(lead)}
                  >
                    {t('common:edit')}
                  </Button>,
                  ...(lead.opportunity ? [] : [
                    <Button 
                      key="opportunity" 
                      type="link" 
                      icon={<PlusOutlined />} 
                      onClick={() => onAddOpportunity(lead)}
                    >
                      {t('sales:opportunities.add', 'Add Opportunity')}
                    </Button>
                  ]),
                  <Button 
                    key="delete" 
                    type="link" 
                    danger
                    icon={<DeleteOutlined />} 
                    onClick={() => onDelete(lead)}
                  >
                    {t('common:delete')}
                  </Button>
                ]}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Paragraph>
                    <Text strong>{t('sales:administratorLeads.table.status')}: </Text>
                    <Tag color={getStatusColor(lead.status)}>
                      {t(`sales:administratorLeads.status.${lead.status.toLowerCase()}`, lead.status)}
                    </Tag>
                  </Paragraph>
                  <Paragraph>
                    <Text strong>{t('sales:administratorLeads.table.email')}: </Text>
                    {primaryContact?.email ? (
                      <Button 
                        type="link" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmailClick(primaryContact.email, lead.name, lead.id);
                        }}
                        style={{ padding: 0, height: 'auto', color: '#1890ff' }}
                      >
                        {primaryContact.email}
                      </Button>
                    ) : '-'}
                  </Paragraph>
                  <Paragraph>
                    <Text strong>{t('sales:administratorLeads.table.phone')}: </Text>
                    {primaryContact?.phone ? (
                      <Space onClick={(e) => e.stopPropagation()}>
                        <Text style={{ color: lead.whatsapp_unavailable ? '#999' : '#52c41a' }}>
                          {primaryContact.phone}
                        </Text>
                        <Button 
                          type="link" 
                          size="small"
                          icon={<PhoneOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCallClick(primaryContact.phone, lead);
                          }}
                          style={{ padding: 0, height: 'auto' }}
                        />
                        <Button 
                          type="link" 
                          size="small"
                          icon={<MessageOutlined />}
                          disabled={lead.whatsapp_unavailable}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWhatsAppClick(primaryContact.phone, lead);
                          }}
                          style={{ padding: 0, height: 'auto' }}
                        />
                        <Button 
                          type="link" 
                          size="small"
                          icon={<TagOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleWhatsApp(lead.id, lead.whatsapp_unavailable || false);
                          }}
                          style={{ padding: 0, height: 'auto' }}
                        />
                        {lead.whatsapp_unavailable && (
                                                  <Tag color="red">
                          {t('sales:administratorLeads.tags.noWhatsApp', 'No WhatsApp')}
                        </Tag>
                        )}
                      </Space>
                    ) : '-'}
                  </Paragraph>
                  <Paragraph>
                    <Text strong>{t('sales:administratorLeads.table.source')}: </Text>
                    <Tag color={getSourceColor(lead.source)}>
                      {lead.source}
                    </Tag>
                  </Paragraph>
                  <Paragraph>
                    <Text strong>{t('sales:administratorLeads.table.created')}: </Text>
                    {formatDateLocale(lead.created_at, lang)}
                  </Paragraph>
                </Space>
              </Card>
            </List.Item>
          );
        }}
      />
    );
  }

  // Safeguard pagination values
  const safePagination = pagination ? {
    ...pagination,
    current: Math.max(1, pagination.current || 1),
    pageSize: Math.max(1, pagination.pageSize || 10),
    total: Math.max(0, pagination.total || 0),
  } : false;

  return (
    <DataTable<AdministratorLead>
      columns={columns}
      data={leads}
      getRowId={(row: AdministratorLead) => row.id}
      loading={loading}
      scroll={{ x: 1000 }}
      onView={onView}
      onEdit={onEdit}
      onDelete={onDelete}
      pagination={safePagination}
      onChange={onTableChange}
      showViewAction={false}
    />
  );
};

export default AdministratorLeadsTable;
